import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { User, Flame, ArrowRight } from 'lucide-react';
import LanguageSwitcher from '../components/Layout/LanguageSwitcher';
import SiteLogo from '../components/Layout/SiteLogo';
import RichTextContent from '../components/RichTextContent';
import AnimatedCounter from '../components/AnimatedCounter';
import { trackPixelEvent } from '../components/MetaPixel';
import LandingQuiz, { PublicQuiz } from '../components/LandingQuiz';
import LegalFooter from '../components/Layout/LegalFooter';
import { getAssetUrl } from '../utils/assetUrl';
import { resolveIcon } from '../utils/iconCatalog';
import {
  getLandingPageHeadline,
  getLandingPageSubheadline,
  getLandingPageBodyHtml,
  getLandingPageCtaLabel,
  getLandingPageUrgencyText,
  getLandingPageCtaMicrocopy,
  getBenefitText,
  getSocialProofLabel,
  getEquipeNome,
  getEquipeCargo,
  getEquipeDescricao
} from '../i18n/localizeContent';

interface Benefit {
  _id?: string;
  order?: number;
  icon?: string;
  i18n?: Record<string, { text?: string }>;
}

interface SocialProofItem {
  _id?: string;
  order?: number;
  value?: string;
  i18n?: Record<string, { label?: string }>;
}

interface LandingPageData {
  _id: string;
  slug: string;
  status: 'draft' | 'published';
  avatar?: { slug: string } | null;
  hero: {
    mediaType: 'image' | 'video';
    image: { url: string };
    video: { url: string };
  };
  benefits: Benefit[];
  socialProof?: SocialProofItem[];
  quiz?: PublicQuiz;
  headline?: string;
  subheadline?: string;
  bodyHtml?: string;
  ctaLabel?: string;
  urgencyText?: string;
  ctaMicrocopy?: string;
  i18n?: any;
  teamMember?: {
    nome: string;
    cargo: string;
    descricao?: string;
    foto?: string;
    i18n?: any;
  } | null;
}

function readPreload(): { locale?: string; landingPage?: LandingPageData; notFound?: boolean } | null {
  try {
    const el = document.getElementById('landing-preload');
    if (!el || !el.textContent) return null;
    return JSON.parse(el.textContent);
  } catch (_err) {
    return null;
  }
}

function isYouTubeOrVimeo(url: string) {
  return /youtube\.com|youtu\.be|vimeo\.com/i.test(url);
}

/** "+500" -> {prefix:'+', value:500, suffix:''}; "98%" -> {prefix:'', value:98, suffix:'%'};
 * "24/7" -> {prefix:'', value:24, suffix:'/7'}. Retorna null se não houver dígitos (ex.: "N/A"),
 * caso em que o valor é exibido como texto simples em vez de contador animado. */
function parseStatValue(raw: string): { prefix: string; value: number; suffix: string } | null {
  const match = raw.match(/^([^\d]*)(\d+)([^\d]*)$/);
  if (!match) return null;
  const [, prefix, digits, suffix] = match;
  return { prefix, value: parseInt(digits, 10), suffix };
}

const LandingPageView: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { t, i18n: i18next } = useTranslation();
  const [searchParams] = useSearchParams();
  const [data, setData] = useState<LandingPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [showStickyCta, setShowStickyCta] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);

  const heroSentinelRef = useRef<HTMLDivElement | null>(null);
  const finalCtaRef = useRef<HTMLDivElement | null>(null);

  const currentLang = (i18next.language?.split('-')[0] || 'pt') as 'pt' | 'en' | 'es';

  useEffect(() => {
    // Um preload só é útil para o slug/idioma exatos desta primeira renderização (SSR) — nunca
    // reaproveitar entre navegações client-side (troca de slug sem reload).
    const preload = readPreload();
    if (preload && preload.landingPage && preload.locale === currentLang) {
      setData(preload.landingPage);
      setLoading(false);
      return;
    }
    if (preload?.notFound) {
      setNotFound(true);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    axios.get(`/landing-pages/public/${slug}`)
      .then((res) => {
        if (!cancelled) setData(res.data);
      })
      .catch(() => {
        if (!cancelled) setNotFound(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, currentLang]);

  const sortedBenefits = useMemo(
    () => (data?.benefits || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data]
  );

  const sortedSocialProof = useMemo(
    () => (data?.socialProof || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [data]
  );

  // Ordem de seções opcionais presentes nesta página específica — usada só para alternar o fundo
  // (branco / cinza-claro) de forma consistente independentemente de quais seções existem.
  const optionalSections = useMemo(() => {
    const list: string[] = [];
    if (!data) return list;
    if (sortedSocialProof.length > 0) list.push('socialProof');
    if (sortedBenefits.length > 0) list.push('benefits');
    if (getLandingPageBodyHtml(data, currentLang)) list.push('body');
    if (data.teamMember) list.push('team');
    return list;
  }, [data, currentLang, sortedSocialProof, sortedBenefits]);
  const sectionBg = (key: string) => (optionalSections.indexOf(key) % 2 === 0 ? 'bg-white' : 'bg-gray-50');

  // Barra de CTA fixa (mobile): visível só depois que o hero sai da tela e some perto do CTA
  // final, pra nunca sobrepor o botão principal. Mesmo padrão de IntersectionObserver do
  // AnimatedCounter — sem lib de animação.
  useEffect(() => {
    if (!data) return undefined;
    const heroEl = heroSentinelRef.current;
    if (!heroEl) return undefined;

    let heroPassed = false;
    let finalCtaVisible = false;
    const updateVisibility = () => setShowStickyCta(heroPassed && !finalCtaVisible);

    const heroObserver = new IntersectionObserver(
      ([entry]) => {
        heroPassed = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        updateVisibility();
      },
      { threshold: 0 }
    );
    heroObserver.observe(heroEl);

    let finalCtaObserver: IntersectionObserver | null = null;
    const finalCtaEl = finalCtaRef.current;
    if (finalCtaEl) {
      finalCtaObserver = new IntersectionObserver(
        ([entry]) => {
          finalCtaVisible = entry.isIntersecting;
          updateVisibility();
        },
        { threshold: 0 }
      );
      finalCtaObserver.observe(finalCtaEl);
    }

    return () => {
      heroObserver.disconnect();
      finalCtaObserver?.disconnect();
    };
  }, [data]);

  const handleCtaClick = () => {
    if (!data) return;
    const utm = {
      source: searchParams.get('utm_source') || '',
      medium: searchParams.get('utm_medium') || '',
      campaign: searchParams.get('utm_campaign') || '',
      term: searchParams.get('utm_term') || '',
      content: searchParams.get('utm_content') || ''
    };
    const fbclid = searchParams.get('fbclid') || '';
    const gclid = searchParams.get('gclid') || '';

    axios.post(`/landing-pages/${data.slug}/click`, {
      utm,
      fbclid,
      gclid,
      referrer: document.referrer,
      locale: currentLang
    }).catch(() => {});
    // Com quiz, o link do grupo só é liberado após a qualificação — não leva mais a /a/:slug.
    if (data.quiz?.enabled && data.quiz.questions?.length) {
      trackPixelEvent('InitiateCheckout', { content_name: data.slug });
      setQuizOpen(true);
      return;
    }

    trackPixelEvent('Lead', { content_name: data.slug });

    const qs = new URLSearchParams({ lp: data.slug, ...(utm.source && { utm_source: utm.source }), ...(fbclid && { fbclid }), ...(gclid && { gclid }) });
    navigate(`/a/${data.avatar?.slug || ''}?${qs.toString()}`);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600" />
      </div>
    );
  }

  if (notFound || !data) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center text-center px-4">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">{t('landingPage.notFound')}</h1>
        <p className="text-gray-600">{t('landingPage.notFoundDesc')}</p>
      </div>
    );
  }

  const headline = getLandingPageHeadline(data, currentLang);
  const subheadline = getLandingPageSubheadline(data, currentLang);
  const bodyHtml = getLandingPageBodyHtml(data, currentLang);
  const ctaLabel = getLandingPageCtaLabel(data, currentLang) || t('landingPage.defaultCta');
  const urgencyText = getLandingPageUrgencyText(data, currentLang);
  const ctaMicrocopy = getLandingPageCtaMicrocopy(data, currentLang);

  const CtaButton = ({ className = '' }: { className?: string }) => (
    <button
      type="button"
      onClick={handleCtaClick}
      className={`btn-primary inline-flex items-center justify-center gap-2 ${className}`}
    >
      {ctaLabel}
      <ArrowRight className="w-5 h-5" />
    </button>
  );

  return (
    <div className="min-h-screen">
      <div className="fixed top-4 right-4 z-40">
        <LanguageSwitcher compact variant="floating" />
      </div>

      {/* Hero */}
      <section className="hero-gradient relative overflow-hidden section-padding">
        <div className="container-custom max-w-3xl text-center relative">
          <div className="mb-8 flex justify-center">
            <SiteLogo size="lg" showName />
          </div>

          {data.hero.mediaType === 'video' && data.hero.video?.url ? (
            isYouTubeOrVimeo(data.hero.video.url) ? (
              <div className="aspect-video w-full mb-8 rounded-2xl overflow-hidden shadow-2xl">
                <iframe
                  src={data.hero.video.url}
                  title={headline}
                  className="w-full h-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <video src={data.hero.video.url} controls className="w-full mb-8 rounded-2xl shadow-2xl" />
            )
          ) : data.hero.image?.url ? (
            <img src={data.hero.image.url} alt={headline} className="w-full mb-8 rounded-2xl shadow-2xl object-cover max-h-96" />
          ) : null}

          <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">{headline}</h1>
          {subheadline && <p className="text-lg sm:text-xl text-gray-600 mb-6">{subheadline}</p>}

          {urgencyText && (
            <div className="inline-flex items-center gap-2 bg-amber-50 text-amber-800 border border-amber-200 rounded-full px-4 py-2 text-sm font-medium mb-8">
              <span className="relative flex h-2 w-2">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-amber-500" />
              </span>
              <Flame className="w-4 h-4 shrink-0" />
              <span>{urgencyText}</span>
            </div>
          )}

          <div className="flex flex-col items-center gap-3">
            <CtaButton className="text-lg px-8 py-4 shadow-xl hover:shadow-2xl hover:scale-[1.03] transition-all duration-200" />
            {ctaMicrocopy && <p className="text-xs text-gray-500">{ctaMicrocopy}</p>}
          </div>
        </div>
        {/* Sentinela para saber quando o hero saiu da viewport (controla o CTA sticky mobile) */}
        <div ref={heroSentinelRef} className="absolute bottom-0 left-0 h-px w-full" aria-hidden="true" />
      </section>

      {/* Prova social */}
      {sortedSocialProof.length > 0 && (
        <section className={`section-padding ${sectionBg('socialProof')}`}>
          <div className="container-custom max-w-4xl">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {sortedSocialProof.map((item, idx) => {
                const label = getSocialProofLabel(item, currentLang);
                if (!item.value && !label) return null;
                const parsed = item.value ? parseStatValue(item.value) : null;
                return (
                  <div key={item._id || idx} className="text-center">
                    <div className="text-3xl sm:text-4xl font-bold text-primary-600 mb-1">
                      {parsed ? (
                        <AnimatedCounter
                          value={parsed.value}
                          prefix={parsed.prefix}
                          suffix={parsed.suffix}
                          className="text-3xl sm:text-4xl font-bold text-primary-600"
                        />
                      ) : (
                        item.value
                      )}
                    </div>
                    {label && <div className="text-sm text-gray-600">{label}</div>}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Benefícios */}
      {sortedBenefits.length > 0 && (
        <section className={`section-padding ${sectionBg('benefits')}`}>
          <div className="container-custom max-w-5xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              {sortedBenefits.map((benefit, idx) => {
                const text = getBenefitText(benefit, currentLang);
                if (!text) return null;
                const BenefitIcon = resolveIcon(benefit.icon);
                return (
                  <div
                    key={benefit._id || idx}
                    className="card flex items-start gap-4 text-left group hover:-translate-y-1 transition-transform duration-300"
                  >
                    <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center shrink-0 group-hover:bg-primary-600 transition-colors duration-300">
                      <BenefitIcon className="w-6 h-6 text-primary-600 group-hover:text-white transition-colors duration-300" />
                    </div>
                    <p className="text-gray-700 pt-2">{text}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Corpo rico */}
      {bodyHtml && (
        <section className={`section-padding ${sectionBg('body')}`}>
          <div className="container-custom max-w-2xl">
            <RichTextContent html={bodyHtml} className="text-left text-gray-700" />
          </div>
        </section>
      )}

      {/* Consultor(a) responsável */}
      {data.teamMember && (
        <section className={`section-padding ${sectionBg('team')}`}>
          <div className="container-custom max-w-md">
            <div className="card flex items-center gap-4 text-left">
              <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-200 flex-shrink-0 border-4 border-white shadow-lg">
                {data.teamMember.foto ? (
                  <img
                    src={getAssetUrl(data.teamMember.foto)}
                    alt={getEquipeNome(data.teamMember, currentLang)}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <User className="w-8 h-8 text-gray-400" />
                  </div>
                )}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{getEquipeNome(data.teamMember, currentLang)}</p>
                <p className="text-sm text-gray-600">{getEquipeCargo(data.teamMember, currentLang)}</p>
                {getEquipeDescricao(data.teamMember, currentLang) && (
                  <RichTextContent
                    html={getEquipeDescricao(data.teamMember, currentLang)}
                    className="text-sm text-gray-500 mt-1 line-clamp-2"
                  />
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA final */}
      <section ref={finalCtaRef} className="section-padding bg-gradient-to-r from-primary-600 to-secondary-600 text-white">
        <div className="container-custom max-w-2xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">{t('landingPage.finalCtaTitle')}</h2>
          <p className="text-lg opacity-90 mb-8">{t('landingPage.finalCtaSubtitle')}</p>
          <div className="flex flex-col items-center gap-3">
            <button
              type="button"
              onClick={handleCtaClick}
              className="bg-white text-primary-600 hover:bg-gray-100 font-semibold text-lg py-4 px-8 rounded-lg shadow-xl hover:shadow-2xl transition-all duration-200 inline-flex items-center justify-center gap-2"
            >
              {ctaLabel}
              <ArrowRight className="w-5 h-5" />
            </button>
            {ctaMicrocopy && <p className="text-xs opacity-80">{ctaMicrocopy}</p>}
          </div>
        </div>
      </section>

      {/* Padding extra no mobile por causa do CTA fixo. */}
      <LegalFooter className="pb-24 md:pb-8" />

      {quizOpen && data.quiz && (
        <LandingQuiz
          slug={data.slug}
          quiz={data.quiz}
          lang={currentLang}
          tracking={{
            utm: {
              source: searchParams.get('utm_source') || '',
              medium: searchParams.get('utm_medium') || '',
              campaign: searchParams.get('utm_campaign') || '',
              term: searchParams.get('utm_term') || '',
              content: searchParams.get('utm_content') || ''
            },
            fbclid: searchParams.get('fbclid') || '',
            gclid: searchParams.get('gclid') || ''
          }}
          onClose={() => setQuizOpen(false)}
        />
      )}

      {/* CTA fixo (mobile) */}
      {showStickyCta && (
        <div className="fixed bottom-0 inset-x-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_16px_rgba(0,0,0,0.1)] p-3 md:hidden">
          <CtaButton className="w-full py-3" />
        </div>
      )}
    </div>
  );
};

export default LandingPageView;
